import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import debounce from 'lodash/debounce';
import styled from 'styled-components';
import { Avatar, CentBasedInputCurrency, Icon, Tag, TooltipIcon, TooltipWrapper, ValidationMessage } from 'components';
import { JobTitleSelect } from 'components/JobTitleSelect/JobTitleSelect';
import { ISelectOption } from 'components/Select/types';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { theme, toRem } from 'design';
import { DisbursementMethod, EmployeeType, HoldbackType, IHoldbackFixed, IHoldbackPercent, IJob, IPayee, IPolicy } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import {
  Bottom,
  CurrencyInputContainer,
  DesktopCurrencyInputContainer,
  DesktopInputContainer,
  FormLayout,
  Header,
  HeaderSection,
  IconButton,
  InputForm,
  Layout,
  Name,
  NetLabel,
  NetLabelWrapper,
  Note,
  NoteInput,
  TopLabel,
  UserInfo,
  UserInfoLayout,
} from './styles';

const Container = styled(Layout)`
  border: 1px solid ${theme.color.neutral[200]};
  min-width: fit-content;
`;

const HeaderError = styled.div`
  justify-content: center;
  width: 100%;
  padding: 8px;
  font-size: ${toRem(12)};
  border-top-left-radius: ${theme.borderRadius.small}px;
  border-top-right-radius: ${theme.borderRadius.small}px;
  background: ${theme.color.warning.light};
`;

export const PaymentsList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  overflow: visible;
  width: auto;
  height: auto;
  list-style: none;
  margin: 0;
`;

const LabelContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  margin-top: 0 !important;

  svg {
    position: absolute;
    left: ${toRem(52)};
    height: ${toRem(15.5)};
    width: ${toRem(15.5)};
  }
`;

const StyledJobTitleSelect = styled(JobTitleSelect)`
  max-width: ${toRem(200)};
  background: none;

  .react-dropdown-select {
    min-height: 0;
    height: ${toRem(22)};
    padding: 0 !important;
    border: none !important;
    background: none;
    text-transform: capitalize;

    &:focus-within {
      background-color: transparent !important;
    }

    .react-dropdown-select-content {
      padding: 0;
    }

    .react-dropdown-select-input {
      background: none;
    }

    .drop-handle {
      padding: 0;
      background: none;
    }
  }

  .react-dropdown-select-dropdown {
    top: ${toRem(20)};
    left: ${toRem(-4)};
  }
`;

const TooltipContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
`;

interface IPaymentsListItemProps {
  field?: string;
  headerError?: string;
  tipAmount?: number;
  hasHoldback?: boolean;
  holdback: number;
  onJobChange: (jobs: ISelectOption<IJob>) => void;
  onTipAmountChange: (tip: number) => void;
  onHoldbackChange: (holdback: number) => void;
  onNoteChange: (note: string) => void;
  onRemove: () => void;
  className?: string;
  isLoading?: boolean;
  note?: string;
  payee: IPayee;
  index: number;
  validateUploadedTips: boolean;
  payeeError: boolean;
  tipAmountError?: string;
  setFieldTouched: (field: string) => void;
  siteId: string;
  sitePayeeLimitMax?: any;
  sitePayeeLimitMin?: any;
  siteHoldbacks: IHoldbackFixed | IHoldbackPercent;
  currentEmployeePolicy?: IPolicy;
  isAcceptancePayment?: boolean;
}

export const PaymentsListItem = ({
  className,
  field = 'payees',
  hasHoldback,
  headerError,
  holdback,
  index,
  isLoading,
  note,
  onHoldbackChange,
  onJobChange,
  onNoteChange,
  onRemove,
  onTipAmountChange,
  payeeError,
  setFieldTouched,
  siteId,
  siteHoldbacks,
  sitePayeeLimitMax,
  sitePayeeLimitMin,
  currentEmployeePolicy,
  tipAmount,
  tipAmountError,
  validateUploadedTips,
  isAcceptancePayment,
  payee,
  ...rest
}: IPaymentsListItemProps) => {
  const [noteVisible, setNoteVisible] = useState<boolean>(!!note);
  const userImage = payee?.user?.avatarUrl;
  const hasPaymentMethod = !!payee?.user?.card;
  const additionalJobTitles = payee.type === EmployeeType.ADMIN ? [{ title: DEFAULT_ORG_ADMIN_JOB_TITLE }] : [];
  const totalPaid = tipAmount ? tipAmount - holdback : 0;

  const handleNote = (value: string) => {
    onNoteChange(value);
  };

  const debounceMs = 500;
  const debouncedSetFieldTouched = debounce(setFieldTouched, debounceMs);

  useEffect(() => {
    // manually set uploaded tip values to `touched` so each field gets validated
    if (validateUploadedTips) {
      setFieldTouched(`${field}[${index}].tipAmount`);
    }
  }, [index, validateUploadedTips, setFieldTouched]);

  const tipAmountTooltip = payeeError && tipAmountError && (
    <TooltipWrapper>
      <ValidationMessage hasError={payeeError}>{tipAmountError}</ValidationMessage>
      <TooltipIcon place="top" iconFill={{ default: theme.color.danger.medium }} iconMarginLeft={0} iconSize={18} id={`amount_error_${index}`}>
        {`${hasHoldback ? 'Net' : 'Amount'} must be between ${sitePayeeLimitMin} and ${sitePayeeLimitMax}.`}
      </TooltipIcon>
    </TooltipWrapper>
  );

  const holdbackToolTip = () => {
    if (!currentEmployeePolicy?.holdbackAdjustment) {
      return (
        <TooltipIcon place="top" id={`holdback_tt_${index}`} dataCy="edit-disabled-tooltip">
          You are not authorized to edit the holdback amounts
        </TooltipIcon>
      );
    }
    if (siteHoldbacks.type === HoldbackType.FIXED) return <></>;

    const holdbackPercent = (siteHoldbacks as IHoldbackPercent).percent;
    const holdbackCap = (siteHoldbacks as IHoldbackPercent).cap;
    return (
      <TooltipIcon place="top" id={`holdback_tt_${index}`}>
        <TooltipContent>
          {holdbackPercent}
          %&nbsp;
          {holdbackCap !== undefined && holdbackCap > 0 && `(max of ${centsToDollarsDisplay(holdbackCap)})`}
        </TooltipContent>
      </TooltipIcon>
    );
  };

  const renderPaymentInputs = (isDesktop: boolean) => {
    const CurrencyContainer = isDesktop ? DesktopCurrencyInputContainer : CurrencyInputContainer;
    return (
      <>
        <CurrencyContainer>
          <TopLabel>{isLoading ? <Skeleton width={61} /> : isAcceptancePayment ? 'Amount' : 'Gross'}</TopLabel>
          <CentBasedInputCurrency
            tabIndex={index + 1}
            width={isDesktop && !hasHoldback ? 240 : undefined}
            name={`${field}[${index}].tipAmount`}
            isLoading={isLoading}
            value={tipAmount?.toString()}
            onChange={(value: string) => {
              onTipAmountChange(parseInt(value, 10));
              debouncedSetFieldTouched(`${field}[${index}].tipAmount`);
            }}
            data-cy="tip-input"
          />
          {tipAmountTooltip}
        </CurrencyContainer>

        {hasHoldback && (
          <>
            <CurrencyContainer>
              <LabelContainer>
                <TopLabel>{isLoading ? <Skeleton width={61} /> : 'Holdback'}</TopLabel>
                {holdbackToolTip()}
              </LabelContainer>
              <CentBasedInputCurrency
                tabIndex={index + 1}
                allowZero
                name={`${field}[${index}].holdbackAmount`}
                isLoading={isLoading}
                value={payee.disbursementMethod === DisbursementMethod.INSTANT ? holdback.toString() : ''}
                data-cy="holdback-input"
                disabled={payee.disbursementMethod !== DisbursementMethod.INSTANT || !currentEmployeePolicy?.holdbackAdjustment}
                onChange={(value: string) => {
                  onHoldbackChange(parseInt(value, 10) || 0);
                  debouncedSetFieldTouched(`${field}[${index}].holdbackAmount`);
                }}
              />
            </CurrencyContainer>
            <CurrencyContainer>
              <TopLabel>{isLoading ? <Skeleton width={61} /> : 'Net'}</TopLabel>
              <NetLabelWrapper>
                <NetLabel data-cy="net-amount">{centsToDollarsDisplay(totalPaid)}</NetLabel>
              </NetLabelWrapper>
            </CurrencyContainer>
          </>
        )}
      </>
    );
  };

  return (
    <Container className={className} {...rest}>
      {headerError && <HeaderError data-cy="header-error">{headerError}</HeaderError>}
      <Header>
        <HeaderSection className="user-info">
          <UserInfoLayout>
            <Avatar data-cy="payee-avatar" size="medlarge" id={payee.id} username={payee.name} image={userImage} hasPaymentMethod={hasPaymentMethod} isLoading={isLoading} />
            <UserInfo>
              <Name data-cy="payee-name">{isLoading ? <Skeleton width={77} /> : payee.name}</Name>
              <StyledJobTitleSelect
                siteId={siteId}
                onChange={onJobChange}
                placeholder="Select a job title"
                defaultValue={{ label: payee.job?.title, value: payee.job }}
                isLoading={isLoading}
                dataCy="payee-job-title-selector"
                additionalJobTitles={additionalJobTitles}
              />
              <Tag label={payee.disbursementMethod} appearance={payee.disbursementMethod} dataCy="payee-disbursement-method" />
            </UserInfo>
          </UserInfoLayout>
        </HeaderSection>

        <HeaderSection className="payment-input-section">
          <DesktopInputContainer hasHoldback={hasHoldback}>{renderPaymentInputs(true)}</DesktopInputContainer>
        </HeaderSection>
        <HeaderSection>
          <IconButton aria-label={noteVisible ? 'Click to hide note.' : 'Click to add note.'} className={noteVisible ? 'note-visible' : ''} onClick={isLoading ? undefined : () => setNoteVisible(!noteVisible)} data-cy="add-note-btn">
            {isLoading ? <Skeleton width={49} /> : <Icon icon="Edit" width={11} height={11} />}
          </IconButton>
          <IconButton aria-label="Remove Payee" data-cy="remove-payee-btn" disabled={isLoading} onClick={onRemove}>
            {isLoading ? <Skeleton circle width={22} height={22} /> : <Icon icon="RemoveThick" width={11} height={11} />}
          </IconButton>
        </HeaderSection>
      </Header>

      <Bottom>
        <InputForm>
          <FormLayout>{renderPaymentInputs(false)}</FormLayout>
        </InputForm>

        <Note
          initial={noteVisible}
          animate={{
            height: noteVisible ? 'auto' : 0,
          }}
          transition={theme.transition.spring.accordion}
          hasHoldback={hasHoldback}
          noteVisible={noteVisible}
        >
          <NoteInput
            type="text"
            data-cy="note-input"
            maxLength={100}
            placeholder="Enter an optional note."
            defaultValue={note}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              handleNote(e.currentTarget.value);
            }}
          />
        </Note>
      </Bottom>
    </Container>
  );
};
